import type { TextChain } from '@/api/types/stowed-element'
import { isSingleFrozenSelectionForUnfreeze } from '@/components/app/editor/select-all-policy'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { lockedObjectBlocksCopy } from '@/lib/editor/clipboard-paste'
import { partitionSelectionForCut } from '@/lib/editor/cut-selection-policy'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isLinkedTextFrame } from '@/lib/groups/linked-text-group-policy'
import { isTextToShapeStructurallyBlocked } from '@/lib/text/text-to-shape-eligibility'

/**
 * Show/hide and enable/disable gates for the object and canvas context menus, derived from the
 * live shape list and selection the caller reads off the shape manager during render, plus the
 * open menu's own target (`targetShapeId`, `selectedShapeIds`).
 */
export function computeContextMenuGates({
  contextMenuShapes,
  contextMenuSelectedShapeIds,
  objectContextMenu,
  linkedTextChains,
}: {
  contextMenuShapes: ShapeData[]
  contextMenuSelectedShapeIds: string[]
  objectContextMenu: { targetShapeId?: string; selectedShapeIds: string[] }
  linkedTextChains: TextChain[] | undefined
}) {
  const contextMenuSelectedShapes = contextMenuShapes.filter((shape) =>
    contextMenuSelectedShapeIds.includes(shape.id)
  )
  const contextMenuSelectedObjectCount = contextMenuSelectedShapes.length
  const contextMenuHasAnyLockedObjects = contextMenuShapes.some((shape) => shape.locked === true)
  const contextMenuHasAnyFrozenObjects = contextMenuShapes.some((shape) => shape.frozen === true)
  const contextMenuSelectionFullyLocked =
    contextMenuSelectedObjectCount > 0 &&
    contextMenuSelectedShapes.every((shape) => shape.locked === true)
  const contextMenuSingleFrozenSelection = isSingleFrozenSelectionForUnfreeze(
    contextMenuSelectedShapes,
    []
  )
  const contextMenuSelectionHasAnyFrozen = contextMenuSelectedShapes.some(
    (shape) => shape.frozen === true
  )
  const contextMenuSelectionHasAnyLocked = contextMenuSelectedShapes.some(
    (shape) => shape.locked === true
  )
  const contextMenuTargetShapeId =
    objectContextMenu.targetShapeId ?? objectContextMenu.selectedShapeIds[0]
  const contextMenuTargetShape = contextMenuTargetShapeId
    ? contextMenuShapes.find((shape) => shape.id === contextMenuTargetShapeId)
    : undefined
  // WMPCM-8911: Edit Path/Clipping Path acts on this single resolved target (see `onEditPath`
  // in the object context menu) — a group has no path geometry of its own, so gate on the target
  // being a group rather than the whole (possibly multi-object) selection. Selecting an object
  // INSIDE a group (isolation mode) resolves to that child shape, not the group, so it stays
  // unaffected.
  const contextMenuTargetIsGroup = Boolean(
    contextMenuTargetShape && isGroupShape(contextMenuTargetShape)
  )
  /**
   * WMPCM-6956: Edit Path and Convert to Shape are both destructive on a chain member —
   * `handleEditPath` deletes and re-adds the element without repairing the chain. `fadd0283`
   * gated them; the TextManager removal (`8c33543a`) dropped the term, leaving a live data-loss
   * path on Edit Path and no gate at all on Convert.
   */
  const contextMenuSelectionHasLinkedTextFrame = contextMenuSelectedShapes.some((shape) =>
    isLinkedTextFrame(shape, linkedTextChains)
  )
  /**
   * WMPCM-10220: visibility for the whole (possibly multi-object) selection, not just the
   * right-clicked target. The old version resolved a single shape — `targetShapeId` if the
   * click landed directly on a shape, else the lone selection member — and hid the row unless
   * THAT ONE shape was eligible. That made the row disappear for a 2+ text-frame multi-select
   * whenever the click landed on the selection's bounding-box chrome instead of a specific shape
   * (`handleSelectionBoxContextMenu` passes no `targetShapeId`) and made it consider only the
   * clicked member's eligibility even when the click did land on a specific shape — while the
   * actual handler (`handleConvertTextToShape` in home.tsx) always converts every eligible
   * member of the selection, not just the target. Mirrors the Object menu's own gate
   * (`convertTextToShapeDisabledForMenus` in home.tsx): visible when at least one selected shape
   * is structurally eligible. `contextMenuSelectionHasLinkedTextFrame` is kept as a separate,
   * selection-wide veto (not folded into "structurally eligible") so a mixed selection
   * containing a chain member still hides the row entirely, per WMPCM-6956.
   */
  const contextMenuConvertTextToShapeVisible =
    !contextMenuSelectionHasLinkedTextFrame &&
    contextMenuSelectedShapes.some(
      (shape) => !isTextToShapeStructurallyBlocked(shape, linkedTextChains)
    )
  /**
   * WMPCM-9514 AC-1: the single object Text on a Path would run along — the right-clicked target
   * if there is one, else a lone selection. Unlike Convert Text to Shape above, this command is
   * deliberately single-target only — see the AC note below.
   *
   * Needed because `showPathCommands` is just `selectedShapeIds.length > 0`: Edit Path silently
   * acts on an arbitrary member of a multi-select, and the AC asks for "a supported object … is
   * selected", singular. Groups are excluded so the command is well-defined; the *messaging* for
   * multi-select and groups is WMPCM-7616 / WMPCM-7623, and locked/frozen policy stays with
   * `disabledItems.textOnPath` (WMPCM-9562 / WMPCM-7632).
   */
  const contextMenuTextOnPathTarget = (() => {
    const target =
      contextMenuShapes.find((shape) => shape.id === objectContextMenu.targetShapeId) ??
      (contextMenuSelectedShapes.length === 1 ? contextMenuSelectedShapes[0] : undefined)

    if (target == null || isGroupShape(target)) return undefined

    return target
  })()
  // Copy-only carve-out: a locked linked frame stays copyable (WMPCM-7012 AC3), so Copy can't reuse
  // `contextMenuSelectionHasAnyLocked` — every other row above still gates on the plain locked state.
  const contextMenuSelectionCopyBlockedByLock = contextMenuSelectedShapes.some((shape) =>
    lockedObjectBlocksCopy(shape, linkedTextChains)
  )
  // Cut skips protected *linked* frames rather than aborting, so the row has to stay enabled while
  // any member is still cuttable — gating on `contextMenuSelectionHasAnyLocked` would make the
  // partial cut unreachable from the menu (WMPCM-8663). This also closes a pre-existing hole: Cut
  // never gated on frozen at all, so a frozen selection offered an enabled row that only failed at
  // runtime.
  //
  // `textBoxes` is empty because a Text-tool frame is a shape now (text-unification) and so is
  // already in `contextMenuSelectedShapes` — the parameter stays because the policy is shared
  // with call sites that still partition a caller-supplied text list.
  const contextMenuCutBlocked =
    partitionSelectionForCut({
      shapes: contextMenuSelectedShapes,
      textBoxes: [],
      chains: linkedTextChains,
    }).kind !== 'proceed'

  return {
    contextMenuSelectedObjectCount,
    contextMenuHasAnyLockedObjects,
    contextMenuHasAnyFrozenObjects,
    contextMenuSelectionFullyLocked,
    contextMenuSingleFrozenSelection,
    contextMenuSelectionHasAnyFrozen,
    contextMenuSelectionHasAnyLocked,
    contextMenuTargetIsGroup,
    contextMenuSelectionHasLinkedTextFrame,
    contextMenuConvertTextToShapeVisible,
    contextMenuTextOnPathTarget,
    contextMenuSelectionCopyBlockedByLock,
    contextMenuCutBlocked,
  }
}
